import { Json } from '@/integrations/supabase/types';

export interface VoiceAssistantTool {
  id: string;
  brand_id: string;
  name: string;
  display_name: string;
  description: string;
  parameters: Json;
  category: string;
  is_active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

export interface OpenAITool {
  type: "function";
  name: string;
  description: string;
  parameters: Record<string, unknown>;
}

// Default tools for seeding
export const DEFAULT_VOICE_TOOLS: Omit<VoiceAssistantTool, 'id' | 'brand_id' | 'created_at' | 'updated_at'>[] = [
  // Business Operations
  {
    name: "getOpenTasks",
    display_name: "Get Open Tasks",
    description: "Get all open tasks for the current brand. Use this when users ask about tasks, to-do items, or what needs to be done.",
    parameters: { type: "object", properties: {}, required: [] },
    category: "business_operations",
    is_active: true,
    priority: 1
  },
  {
    name: "getSquadMembers",
    display_name: "Get Squad Members",
    description: "Get team members for the current brand. Use this when users ask about the team, who is working on what, or team capacity.",
    parameters: { type: "object", properties: {}, required: [] },
    category: "business_operations",
    is_active: true,
    priority: 2
  },
  {
    name: "getSprintStatus",
    display_name: "Get Sprint Status",
    description: "Get current sprint information and progress. Use this when users ask about sprint progress, current sprint, or timeline.",
    parameters: { type: "object", properties: {}, required: [] },
    category: "business_operations",
    is_active: true,
    priority: 3
  },
  {
    name: "getTaskDetails",
    display_name: "Get Task Details",
    description: "Get detailed information about a specific task. Use this when users ask about a particular task.",
    parameters: {
      type: "object",
      properties: {
        taskId: { type: "string", description: "The ID of the task" },
        taskType: { type: "string", description: "The type of task (foundational_tasks, growth_tasks, or recurring_tasks)" }
      },
      required: ["taskId", "taskType"]
    },
    category: "business_operations",
    is_active: true,
    priority: 4
  },
  {
    name: "updateTaskStatus",
    display_name: "Update Task Status",
    description: "Update the status of a task. Use this when users want to mark tasks as complete, in progress, etc.",
    parameters: {
      type: "object",
      properties: {
        taskId: { type: "string", description: "The ID of the task" },
        taskType: { type: "string", description: "The type of task" },
        newStatus: { type: "string", description: "The new status for the task" }
      },
      required: ["taskId", "taskType", "newStatus"]
    },
    category: "business_operations",
    is_active: true,
    priority: 5
  },
  {
    name: "getBusinessMetrics",
    display_name: "Get Business Metrics",
    description: "Get recent business metrics and performance data. Use this when users ask about performance, metrics, KPIs, or business data.",
    parameters: { type: "object", properties: {}, required: [] },
    category: "business_operations",
    is_active: true,
    priority: 6
  },
  {
    name: "createTask",
    display_name: "Create Task",
    description: "Create a new task. Use this when users want to add a new task, to-do item, or action item.",
    parameters: {
      type: "object",
      properties: {
        title: { type: "string", description: "The title of the task" },
        description: { type: "string", description: "Optional description of the task" },
        taskType: { type: "string", description: "The type of task: foundational_tasks, growth_tasks, or recurring_tasks. Default is foundational_tasks" },
        priority: { type: "string", description: "Priority level: high, medium, or low. Default is medium" },
        dueDate: { type: "string", description: "Optional due date in ISO format (YYYY-MM-DD)" }
      },
      required: ["title"]
    },
    category: "business_operations",
    is_active: true,
    priority: 7
  },
  // SEO & Content
  {
    name: "seoResearch",
    display_name: "SEO Research",
    description: "Research keywords, competitors, and content opportunities for a topic. Use this when users ask about keyword research, SEO opportunities, or want to research a topic before writing content.",
    parameters: {
      type: "object",
      properties: {
        topic: { type: "string", description: "The topic or keyword to research" }
      },
      required: ["topic"]
    },
    category: "seo_content",
    is_active: true,
    priority: 10
  },
  {
    name: "seoWrite",
    display_name: "SEO Write",
    description: "Create an SEO-optimized long-form article on a topic. Use this when users want to write a blog post, article, or content piece.",
    parameters: {
      type: "object",
      properties: {
        topic: { type: "string", description: "The topic to write about" },
        researchBrief: { type: "string", description: "Optional research brief or context to guide the writing" }
      },
      required: ["topic"]
    },
    category: "seo_content",
    is_active: true,
    priority: 11
  },
  {
    name: "seoAnalyze",
    display_name: "SEO Analyze",
    description: "Analyze existing content or a URL for SEO improvements. Use this when users want to analyze their current content or a competitor's content.",
    parameters: {
      type: "object",
      properties: {
        url: { type: "string", description: "The URL to analyze" },
        filePath: { type: "string", description: "Optional file path to analyze instead of URL" }
      },
      required: []
    },
    category: "seo_content",
    is_active: true,
    priority: 12
  },
  {
    name: "seoOptimize",
    display_name: "SEO Optimize",
    description: "Perform a final SEO optimization pass on content. Use this when users want to polish and optimize their content for search engines.",
    parameters: {
      type: "object",
      properties: {
        filePath: { type: "string", description: "The file path of the content to optimize" }
      },
      required: ["filePath"]
    },
    category: "seo_content",
    is_active: true,
    priority: 13
  },
  {
    name: "seoRewrite",
    display_name: "SEO Rewrite",
    description: "Rewrite and update existing content for better SEO performance. Use this when users want to refresh or improve old content.",
    parameters: {
      type: "object",
      properties: {
        topic: { type: "string", description: "The topic of the content to rewrite" },
        analysisFile: { type: "string", description: "Optional analysis file to guide the rewrite" }
      },
      required: ["topic"]
    },
    category: "seo_content",
    is_active: true,
    priority: 14
  }
];

export const VOICE_TOOL_CATEGORIES: Record<string, string> = {
  business_operations: "Business Operations",
  seo_content: "SEO & Content",
  analytics: "Analytics",
  communication: "Communication",
  general: "General"
};
